/* BMN Enriched PDP — position-first decoration configurator + live pricing engine.
 *
 * One reusable module (also the front door to Mockup Tool v5). Reads the rate
 * card + blank price from data attributes on .bmn-epdp (all editable in theme
 * settings, nothing hard-coded), computes per-shirt and order totals with
 * application-based tiers (total units x positions) matching the BMN quote-builder,
 * plus one-off new-logo setup, and carries the full spec to cart
 * as line-item properties. Supply-later / free-mockup / WhatsApp route the spec
 * to the quote lead path (HubSpot). Prices are ex VAT; each money value is
 * rendered as dual .vat-inc/.vat-ex spans so the header VAT toggle switches them.
 */
(function () {
  'use strict';

  var COLOUR_MAP = {'black':'#1a1a1a','jet black':'#0d0d0d','white':'#ffffff','arctic white':'#ffffff','natural':'#efe7d6','ecru':'#efe7d6','navy':'#20294a','french navy':'#1b2440','oxford navy':'#1b2440','red':'#c0202a','classic red':'#c0202a','burgundy':'#6d1f2e','maroon':'#5e1a2b','royal':'#1f4fb0','royal blue':'#1f4fb0','sapphire':'#1e63a8','sky':'#7fb9e6','sky blue':'#7fb9e6','light blue':'#a9cde8','turquoise':'#2bb6c4','teal':'#1f8a8a','bottle green':'#0f3b2e','bottle':'#0f3b2e','forest':'#16432c','kelly':'#1f8f48','green':'#1f8f48','lime':'#9cc63b','olive':'#6b6a2f','sage':'#9bae8e','charcoal':'#3a3a3a','dark grey':'#4a4a4a','graphite':'#4a4a4a','heather grey':'#9b9b9b','grey':'#9b9b9b','gray':'#9b9b9b','sport grey':'#a8a8a8','light grey':'#cfcfcf','silver':'#c8c8c8','ash':'#d7d7d2','purple':'#5b3a8a','plum':'#5e2a4d','pink':'#e58fb0','light pink':'#f1c4d4','rose':'#d98aa0','coral':'#f08a6d','yellow':'#f4d03f','gold':'#e3b341','sunflower':'#f4c430','orange':'#e8732a','brown':'#5a3d2b'};

  var POSITIONS = [
    { id: 'lc', label: 'Left chest',   method: 'embroidery' },
    { id: 'rc', label: 'Right chest',  method: 'embroidery' },
    { id: 'cc', label: 'Centre chest', method: 'embroidery' },
    { id: 'np', label: 'Nape / neck',  method: 'embroidery' },
    { id: 'ub', label: 'Upper back',   method: 'dtf' },
    { id: 'fb', label: 'Full back',    method: 'dtf', oversized: true },
    { id: 'ls', label: 'Left sleeve',  method: 'embroidery' },
    { id: 'rs', label: 'Right sleeve', method: 'embroidery' }
  ];

  // Tier by TOTAL APPLICATIONS (total units x number of positions), matching the
  // BMN quote-builder. Bands differ by method.
  function embBand(apps) { if (apps < 50) return 0; if (apps < 250) return 1; if (apps < 500) return 2; return 3; }        // 1-49 / 50-249 / 250-499 / 500+
  function dtfBand(apps) { if (apps < 9) return 0; if (apps < 25) return 1; if (apps < 100) return 2; if (apps < 250) return 3; if (apps < 500) return 4; return 5; } // 1-8 / 9-24 / 25-99 / 100-249 / 250-499 / 500+

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function EnrichedPDP(root) {
    this.root = root;
    this.blank = parseInt(root.getAttribute('data-blank-price'), 10) || 0;          // pence, ex VAT
    this.sizeIndex = parseInt(root.getAttribute('data-size-index'), 10);
    this.colourIndex = parseInt(root.getAttribute('data-colour-index'), 10);
    this.setupFee = parseInt(root.getAttribute('data-setup-fee'), 10) || 0;         // pence
    this.whatsapp = root.getAttribute('data-whatsapp') || '';
    this.leadUrl = root.getAttribute('data-lead-url') || '';
    this.setupVariantId = root.getAttribute('data-setup-variant') || '';
    this.title = root.getAttribute('data-product-title') || '';
    this.handle = root.getAttribute('data-product-handle') || '';
    this.repeat = false;
    try { this.rates = JSON.parse(root.getAttribute('data-rates') || '{}'); } catch (e) { this.rates = {}; }
    // rates in POUNDS from settings -> pence. Tiers keyed by total applications.
    this.ratesP = {
      embStd:  (this.rates.embStd  || [0,0,0,0]).map(toPence),
      embHigh: (this.rates.embHigh || [0,0,0,0]).map(toPence),
      dtf:     (this.rates.dtf     || [0,0,0,0,0,0]).map(toPence),
      oversizedEmb: toPence(this.rates.oversizedEmb),
      oversizedDtf: toPence(this.rates.oversizedDtf)
    };

    var vd = root.querySelector('.bmn-epdp__variant-data');
    this.variants = vd ? JSON.parse(vd.textContent) : [];
    this.variantMap = {};
    var self = this;
    this.variants.forEach(function (v) { self.variantMap[v.options.join('|')] = v; });

    // state
    this.colour = null;
    this.selected = {};   // id -> { method, source, text, fileName }

    this._cache();
    this._applyColourSwatches();
    this._bind();
    // default colour = first available swatch
    var firstSw = root.querySelector('.bmn-epdp__swatch');
    if (firstSw) this._selectColour(firstSw);
    this._resolveSizes();
    this._recompute();
  }

  function toPence(v) { return Math.round((parseFloat(v) || 0) * 100); }

  EnrichedPDP.prototype._cache = function () {
    var r = this.root;
    this.swatches = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__swatch'));
    this.colourLabel = r.querySelector('.bmn-epdp__colour-current');
    this.sizeRows = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__size-row'));
    this.qtyInputs = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__qty'));
    this.posButtons = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__pos'));
    this.linesEl = r.querySelector('.bmn-epdp__lines');
    this.linesEmpty = r.querySelector('.bmn-epdp__lines-empty');
    this.elTotalUnits = r.querySelectorAll('.bmn-epdp__total-units');
    this.elPerShirt = r.querySelector('.bmn-epdp__per-shirt');
    this.elOrderTotal = r.querySelectorAll('.bmn-epdp__order-total');
    this.elSetup = r.querySelector('.bmn-epdp__setup-line');
    this.elTier = r.querySelector('.bmn-epdp__tier');
    this.elRec = r.querySelector('.bmn-epdp__rec');
    this.elBreak = r.querySelector('.bmn-epdp__breaks');
    this.addBtns = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__add'));
    this.msg = r.querySelector('.bmn-epdp__msg');
    this.sticky = r.querySelector('.bmn-epdp__sticky');
    this.waBtn = r.querySelector('.bmn-epdp__whatsapp');
    this.mockupBtns = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__mockup'));
    this.lead = r.querySelector('.bmn-epdp__lead');
    this.repeatToggle = r.querySelector('.bmn-epdp__repeat');
  };

  EnrichedPDP.prototype._applyColourSwatches = function () {
    this.swatches.forEach(function (sw) {
      var n = (sw.getAttribute('data-value') || '').toLowerCase().trim();
      var hex = COLOUR_MAP[n];
      if (!hex) { for (var k in COLOUR_MAP) { if (n.indexOf(k) !== -1) { hex = COLOUR_MAP[k]; break; } } }
      if (hex) {
        sw.style.background = hex;
        if (hex === '#ffffff') sw.style.boxShadow = 'inset 0 0 0 1px rgba(15,15,15,0.25)';
      }
    });
  };

  EnrichedPDP.prototype._bind = function () {
    var self = this;
    this.swatches.forEach(function (sw) { sw.addEventListener('click', function () { self._selectColour(sw); }); });
    this.qtyInputs.forEach(function (i) {
      i.addEventListener('input', function () { self._clamp(i); self._recompute(); self._clearMsg(); });
      i.addEventListener('change', function () { self._clamp(i); self._recompute(); });
    });
    this.root.querySelectorAll('.bmn-epdp__step').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = btn.parentNode.querySelector('.bmn-epdp__qty');
        var d = parseInt(btn.getAttribute('data-step'), 10) || 0;
        input.value = Math.max(0, (parseInt(input.value, 10) || 0) + d);
        self._clamp(input); self._recompute();
      });
    });
    this.posButtons.forEach(function (b) { b.addEventListener('click', function () { self._togglePosition(b.getAttribute('data-id')); }); });
    this.addBtns.forEach(function (b) { b.addEventListener('click', function () { self._addToCart(); }); });
    if (this.waBtn) this.waBtn.addEventListener('click', function () { self._whatsapp(); });
    this.mockupBtns.forEach(function (b) { b.addEventListener('click', function () { self._openLead('mockup'); }); });
    // delegated events on branding lines
    if (this.linesEl) {
      this.linesEl.addEventListener('click', function (e) {
        var mBtn = e.target.closest('.bmn-epdp__method-btn');
        if (mBtn) { self._setMethod(mBtn.getAttribute('data-pos'), mBtn.getAttribute('data-method')); return; }
        var rm = e.target.closest('.bmn-epdp__line-remove');
        if (rm) { self._togglePosition(rm.getAttribute('data-pos')); return; }
      });
      this.linesEl.addEventListener('change', function (e) {
        var src = e.target.closest('.bmn-epdp__source');
        if (src) { self._setSource(src.getAttribute('data-pos'), src.value); return; }
        var hi = e.target.closest('.bmn-epdp__high');
        if (hi) { var sh = self.selected[hi.getAttribute('data-pos')]; if (sh) { sh.highStitch = hi.checked; self._renderLines(); self._recompute(); } return; }
        var ov = e.target.closest('.bmn-epdp__oversized');
        if (ov) { var so = self.selected[ov.getAttribute('data-pos')]; if (so) { so.oversized = ov.checked; self._renderLines(); self._recompute(); } return; }
        var file = e.target.closest('.bmn-epdp__file');
        if (file) { var s = self.selected[file.getAttribute('data-pos')]; if (s) { s.fileName = (file.files[0] || {}).name || ''; self._renderLines(); self._recompute(); } return; }
      });
      this.linesEl.addEventListener('input', function (e) {
        var txt = e.target.closest('.bmn-epdp__text');
        if (txt) { var s = self.selected[txt.getAttribute('data-pos')]; if (s) s.text = txt.value; }
      });
    }
    if (this.lead) {
      var form = this.lead.querySelector('form');
      if (form) form.addEventListener('submit', function (e) { e.preventDefault(); self._submitLead(); });
      var close = this.lead.querySelector('.bmn-epdp__lead-close');
      if (close) close.addEventListener('click', function () { self.lead.hidden = true; });
    }
    if (this.repeatToggle) this.repeatToggle.addEventListener('change', function () { self.repeat = self.repeatToggle.checked; self._recompute(); });
  };

  EnrichedPDP.prototype._clamp = function (i) {
    var v = parseInt(i.value, 10); if (isNaN(v) || v < 0) v = 0; i.value = v;
    i.classList.toggle('is-active', v > 0);
  };

  EnrichedPDP.prototype._selectColour = function (sw) {
    this.swatches.forEach(function (s) { s.classList.remove('is-selected'); s.setAttribute('aria-pressed', 'false'); });
    sw.classList.add('is-selected'); sw.setAttribute('aria-pressed', 'true');
    this.colour = sw.getAttribute('data-value');
    if (this.colourLabel) this.colourLabel.textContent = this.colour;
    this._setGalleryImage(sw.getAttribute('data-media-id'));
    this._resolveSizes();
    this._recompute();
  };

  EnrichedPDP.prototype._setGalleryImage = function (mediaId) {
    if (!mediaId) return;
    var gallery = document.querySelector('media-gallery'); if (!gallery) return;
    var slide = Array.prototype.slice.call(gallery.querySelectorAll('[data-media-id]')).find(function (el) { return el.dataset.mediaId && el.dataset.mediaId.split('-').pop() === String(mediaId); });
    if (slide && typeof gallery.setActiveMedia === 'function') { try { gallery.setActiveMedia(slide.dataset.mediaId, true); } catch (e) {} }
  };

  EnrichedPDP.prototype._resolveSizes = function () {
    var self = this;
    this.sizeRows.forEach(function (row) {
      var opts = new Array(self.variants.length ? self.variants[0].options.length : 2);
      opts[self.sizeIndex] = row.getAttribute('data-size');
      if (!isNaN(self.colourIndex)) opts[self.colourIndex] = self.colour;
      var v = self.variantMap[opts.join('|')];
      var input = row.querySelector('.bmn-epdp__qty');
      var badge = row.querySelector('.bmn-epdp__size-badge');
      if (v) {
        row.setAttribute('data-variant-id', v.id); input.setAttribute('data-variant-id', v.id);
        input.disabled = !v.available; if (!v.available) { input.value = 0; input.classList.remove('is-active'); }
        row.classList.toggle('is-unavailable', !v.available);
        if (badge) badge.textContent = v.available ? 'In stock' : 'Out of stock';
      } else {
        input.disabled = true; row.classList.add('is-unavailable'); if (badge) badge.textContent = '—';
      }
    });
  };

  EnrichedPDP.prototype._togglePosition = function (id) {
    if (this.selected[id]) { delete this.selected[id]; }
    else {
      var def = POSITIONS.filter(function (p) { return p.id === id; })[0] || { method: 'embroidery' };
      this.selected[id] = { method: def.method, source: 'later', text: '', fileName: '', highStitch: false, oversized: !!def.oversized };
    }
    this.posButtons.forEach(function (b) {
      var on = b.getAttribute('data-id') in this.selected;
      b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    }, this);
    this._renderLines();
    this._recompute();
  };

  EnrichedPDP.prototype._setMethod = function (id, method) {
    if (this.selected[id]) { this.selected[id].method = method; this._renderLines(); this._recompute(); }
  };
  EnrichedPDP.prototype._setSource = function (id, source) {
    if (this.selected[id]) { this.selected[id].source = source; this._renderLines(); this._recompute(); }
  };

  EnrichedPDP.prototype._orderedSelected = function () {
    var self = this;
    return POSITIONS.filter(function (p) { return self.selected[p.id]; }).map(function (p) {
      var s = self.selected[p.id];
      return { id: p.id, label: p.label, method: s.method, source: s.source, text: s.text, fileName: s.fileName, highStitch: !!s.highStitch, oversized: !!s.oversized };
    });
  };

  // total applications = total units x number of decoration positions
  EnrichedPDP.prototype._apps = function () { return this._totalUnits() * this._orderedSelected().length; };

  // per-position rate (pence) at a given total-applications count
  EnrichedPDP.prototype._rateForLine = function (l, apps) {
    var r = this.ratesP;
    if (l.oversized) return l.method === 'dtf' ? r.oversizedDtf : r.oversizedEmb;
    if (l.method === 'dtf') return r.dtf[dtfBand(apps)] || 0;
    return (l.highStitch ? r.embHigh : r.embStd)[embBand(apps)] || 0;
  };

  EnrichedPDP.prototype._renderLines = function () {
    if (!this.linesEl) return;
    var lines = this._orderedSelected();
    if (this.linesEmpty) this.linesEmpty.hidden = lines.length > 0;
    var apps = this._apps();
    var self = this;
    this.linesEl.innerHTML = lines.map(function (l) {
      var uplift = self._rateForLine(l, apps);
      var flags = '';
      if (l.method === 'embroidery' && !l.oversized) {
        flags += '<label class="bmn-epdp__flag"><input type="checkbox" class="bmn-epdp__high" data-pos="' + l.id + '"' + (l.highStitch ? ' checked' : '') + '> Large / detailed logo (high stitch)</label>';
      }
      flags += '<label class="bmn-epdp__flag"><input type="checkbox" class="bmn-epdp__oversized" data-pos="' + l.id + '"' + (l.oversized ? ' checked' : '') + '> Oversized / big back (flat rate)</label>';
      return '' +
        '<div class="bmn-epdp__line" data-pos="' + l.id + '">' +
          '<div class="bmn-epdp__line-head">' +
            '<strong>' + esc(l.label) + '</strong>' +
            '<button type="button" class="bmn-epdp__line-remove" data-pos="' + l.id + '" aria-label="Remove ' + esc(l.label) + '">Remove</button>' +
          '</div>' +
          '<div class="bmn-epdp__line-row">' +
            '<div class="bmn-epdp__method" role="group" aria-label="Decoration method">' +
              '<button type="button" class="bmn-epdp__method-btn' + (l.method === 'embroidery' ? ' is-on' : '') + '" data-pos="' + l.id + '" data-method="embroidery">Embroidery</button>' +
              '<button type="button" class="bmn-epdp__method-btn' + (l.method === 'dtf' ? ' is-on' : '') + '" data-pos="' + l.id + '" data-method="dtf">DTF print</button>' +
            '</div>' +
            '<span class="bmn-epdp__line-uplift">+ ' + self._dual(uplift) + ' <small>each</small></span>' +
          '</div>' +
          '<div class="bmn-epdp__flags">' + flags + '</div>' +
          '<div class="bmn-epdp__artwork">' +
            '<select class="bmn-epdp__source" data-pos="' + l.id + '" aria-label="Artwork for ' + esc(l.label) + '">' +
              '<option value="later"' + (l.source === 'later' ? ' selected' : '') + '>Supply logo later (free mockup)</option>' +
              '<option value="upload"' + (l.source === 'upload' ? ' selected' : '') + '>Upload logo now</option>' +
              '<option value="text"' + (l.source === 'text' ? ' selected' : '') + '>Add text / name</option>' +
            '</select>' +
            (l.source === 'upload' ? '<label class="bmn-epdp__file-wrap"><input type="file" class="bmn-epdp__file" data-pos="' + l.id + '" accept="image/*,.pdf,.ai,.eps,.svg" /><span>' + (l.fileName ? esc(l.fileName) : 'Choose file') + '</span></label>' : '') +
            (l.source === 'text' ? '<input type="text" class="bmn-epdp__text" data-pos="' + l.id + '" value="' + esc(l.text) + '" placeholder="Name or text to decorate" maxlength="40" />' : '') +
          '</div>' +
        '</div>';
    }).join('');
  };

  EnrichedPDP.prototype._totalUnits = function () {
    return this.qtyInputs.reduce(function (s, i) { return s + (i.disabled ? 0 : (parseInt(i.value, 10) || 0)); }, 0);
  };

  EnrichedPDP.prototype._recompute = function () {
    var total = this._totalUnits();
    var lines = this._orderedSelected();
    var apps = total * lines.length;
    var perShirtDecoration = 0;
    var self = this;
    lines.forEach(function (l) { perShirtDecoration += self._rateForLine(l, apps); });
    var perShirt = this.blank + perShirtDecoration;
    var subtotal = perShirt * total;
    var setup = (lines.length > 0 && !this.repeat) ? this.setupFee : 0;
    var orderTotal = subtotal + setup;

    // refresh per-line uplifts (band changes with total applications)
    this.root.querySelectorAll('.bmn-epdp__line').forEach(function (lineEl) {
      var id = lineEl.getAttribute('data-pos'); var s = self.selected[id]; if (!s) return;
      var up = self._rateForLine({ method: s.method, oversized: !!s.oversized, highStitch: !!s.highStitch }, apps);
      var u = lineEl.querySelector('.bmn-epdp__line-uplift');
      if (u) u.innerHTML = '+ ' + self._dual(up) + ' <small>each</small>';
    });

    this.elTotalUnits.forEach(function (el) { el.textContent = total; });
    if (this.elPerShirt) this.elPerShirt.innerHTML = this._dual(perShirt);
    this.elOrderTotal.forEach(function (el) { el.innerHTML = self._dual(orderTotal); });
    if (this.elTier) {
      this.elTier.textContent = (total > 0 && lines.length)
        ? (apps + ' application' + (apps !== 1 ? 's' : '') + ' (' + total + ' unit' + (total !== 1 ? 's' : '') + ' × ' + lines.length + ' position' + (lines.length !== 1 ? 's' : '') + ')')
        : '';
    }
    if (this.elSetup) {
      if (!lines.length) this.elSetup.textContent = '';
      else if (setup === 0) this.elSetup.textContent = 'No new-logo setup (repeat order).';
      else this.elSetup.textContent = 'New-logo setup: ' + this._plain(setup) + ' one-off, free on repeat orders.';
    }
    this._renderBreaks(lines, total);
    this._renderRecommendation(lines);
    if (this.sticky) this.sticky.classList.toggle('is-ready', total > 0 && lines.length > 0);
    this.addBtns.forEach(function (b) { b.disabled = total === 0; });
  };

  EnrichedPDP.prototype._renderBreaks = function (lines, total) {
    if (!this.elBreak) return;
    if (!lines.length) { this.elBreak.innerHTML = ''; return; }
    var self = this;
    var mil = [10, 25, 50, 100, 250, 500];
    var rows = mil.map(function (units, i) {
      var apps = units * lines.length;
      var dec = 0; lines.forEach(function (l) { dec += self._rateForLine(l, apps); });
      var ps = self.blank + dec;
      var next = mil[i + 1];
      var active = total >= units && (next == null || total < next);
      return '<tr' + (active ? ' class="is-active"' : '') + '><td>' + units + '+ units</td><td>' + self._dual(ps) + ' <small>each</small></td></tr>';
    }).join('');
    this.elBreak.innerHTML = '<table class="bmn-epdp__break-table"><thead><tr><th>Order size</th><th>Per shirt, decorated (<span class="vat-inc">inc VAT</span><span class="vat-ex">ex VAT</span>)</th></tr></thead><tbody>' + rows + '</tbody></table>';
  };

  EnrichedPDP.prototype._renderRecommendation = function (lines) {
    if (!this.elRec) return;
    var hasChest = lines.some(function (l) { return /chest|nape/.test(l.id) || ['lc','rc','cc','np'].indexOf(l.id) !== -1; });
    var hasBack = lines.some(function (l) { return ['ub','fb'].indexOf(l.id) !== -1; });
    var tips = [];
    if (hasChest) tips.push('Chest and nape logos look sharpest embroidered.');
    if (hasBack) tips.push('Large back artwork is best as a DTF print (full colour, no stitch limit).');
    if (!lines.length) tips.push('Most workwear uses a left-chest logo. Add positions above to price it up.');
    tips.push('Lightweight 100% polyester (160gsm or under) prints better than it embroiders, we will flag this on your proof.');
    this.elRec.innerHTML = tips.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('');
  };

  // ---------- money ----------
  function fmt(pence, withCurrency) {
    var v = pence / 100;
    var format = (window.BMNVat && (withCurrency ? window.BMNVat.moneyCurrencyFormat : window.BMNVat.moneyFormat)) || '£{{amount}}';
    var amount = v.toFixed(2);
    return format.replace(/\{\{\s*(amount|amount_with_comma_separator|amount_no_decimals|amount_no_decimals_with_comma_separator)\s*\}\}/g, function (m, t) {
      if (t.indexOf('no_decimals') !== -1) return String(Math.round(v));
      return amount;
    });
  }
  EnrichedPDP.prototype._dual = function (pence) {
    var wc = window.BMNVat ? window.BMNVat.currencyCodeEnabled : false;
    var inc = Math.round(pence * 1.2);
    return '<span class="vat-inc">' + fmt(inc, wc) + '</span><span class="vat-ex">' + fmt(pence, wc) + '</span>';
  };
  EnrichedPDP.prototype._plain = function (pence) { return fmt(pence, false); };

  // ---------- spec ----------
  EnrichedPDP.prototype._specText = function () {
    var lines = this._orderedSelected();
    var sizes = this.sizeRows.map(function (row) {
      var q = parseInt(row.querySelector('.bmn-epdp__qty').value, 10) || 0;
      return q > 0 ? (row.getAttribute('data-size') + ' x' + q) : null;
    }).filter(Boolean).join(', ');
    var dec = lines.map(function (l) {
      var src = l.source === 'upload' ? ('uploaded' + (l.fileName ? ': ' + l.fileName : '')) : (l.source === 'text' ? ('text: ' + (l.text || '')) : 'supply later');
      var method = l.method === 'dtf' ? 'DTF print' : ('Embroidery' + (l.highStitch ? ' (high stitch)' : ''));
      if (l.oversized) method += ', oversized';
      return l.label + ' — ' + method + ' (' + src + ')';
    }).join('; ');
    return { sizes: sizes, dec: dec || 'none yet', lines: lines };
  };

  EnrichedPDP.prototype._properties = function () {
    var t = this._specText();
    var total = this._totalUnits();
    var apps = total * t.lines.length;
    var perShirtDecoration = 0, self = this;
    t.lines.forEach(function (l) { perShirtDecoration += self._rateForLine(l, apps); });
    var perShirt = this.blank + perShirtDecoration;
    var subtotal = perShirt * total;
    var setup = (t.lines.length > 0 && !this.repeat) ? this.setupFee : 0;
    return {
      'Colour': this.colour || '',
      'Sizes': t.sizes,
      'Applications': apps + ' (' + total + ' x ' + t.lines.length + ')',
      'Decoration': t.dec,
      'Per shirt (ex VAT)': this._plain(perShirt),
      'Order total (ex VAT)': this._plain(subtotal + setup),
      'Setup': setup ? (this._plain(setup) + ' new-logo setup') : 'none (repeat order)',
      '_spec': JSON.stringify({ product: { handle: this.handle, title: this.title }, colour: this.colour, positions: t.lines, totalUnits: total, applications: apps, repeat: this.repeat })
    };
  };

  // ---------- cart ----------
  EnrichedPDP.prototype._buildItems = function () {
    var props = this._properties();
    var items = [];
    this.qtyInputs.forEach(function (i) {
      var q = parseInt(i.value, 10) || 0;
      if (q > 0 && i.getAttribute('data-variant-id')) items.push({ id: parseInt(i.getAttribute('data-variant-id'), 10), quantity: q, properties: props });
    });
    return items;
  };

  EnrichedPDP.prototype._addToCart = function () {
    var self = this;
    var lines = this._orderedSelected();
    var total = this._totalUnits();
    if (total === 0) { this._say('Add a size quantity first.', 'error'); return; }
    // any position needing artwork now? if all are "later", route to quote instead
    var needsArt = lines.some(function (l) { return l.source === 'upload' && !l.fileName; });
    if (needsArt) { this._say('Choose a file for each "upload" position, or switch it to "supply later".', 'error'); return; }
    var allLater = lines.length > 0 && lines.every(function (l) { return l.source === 'later'; });
    if (allLater) { this._openLead('later'); return; }

    var items = this._buildItems();
    // setup fee line, if a variant is configured and applicable
    if (this.setupVariantId) {
      var props = this._properties();
      if (/new-logo setup$/.test(props['Setup'])) items.push({ id: parseInt(this.setupVariantId, 10), quantity: 1, properties: { 'For order': this.title } });
    }
    this.addBtns.forEach(function (b) { b.classList.add('is-loading'); b.disabled = true; });
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items }) })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        if (!res.ok) { self._say((res.d && (res.d.description || res.d.message)) || 'Could not add to cart.', 'error'); return; }
        self._say('Added ' + total + ' item' + (total !== 1 ? 's' : '') + ' with your decoration spec. Redirecting to cart...', 'success');
        self._refreshCart();
        setTimeout(function () { window.location.href = '/cart'; }, 900);
      })
      .catch(function () { self._say('Something went wrong. Please try again or use WhatsApp quote.', 'error'); })
      .finally(function () { self.addBtns.forEach(function (b) { b.classList.remove('is-loading'); b.disabled = self._totalUnits() === 0; }); });
  };

  EnrichedPDP.prototype._refreshCart = function () {
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (c) {
      document.querySelectorAll('.cart-count-bubble').forEach(function (b) { b.hidden = c.item_count === 0; var v = b.querySelector('[aria-hidden="true"]'); if (v) v.textContent = c.item_count; });
    }).catch(function () {});
  };

  // ---------- quote / lead ----------
  EnrichedPDP.prototype._quoteMessage = function () {
    var t = this._specText();
    var total = this._totalUnits();
    return 'Quote request from the product page.\n' +
      'Product: ' + this.title + ' (' + this.handle + ')\n' +
      'Colour: ' + (this.colour || '-') + '\n' +
      'Sizes: ' + (t.sizes || '-') + '  (total ' + total + ')\n' +
      'Decoration: ' + t.dec + '\n' +
      'Please send an itemised branded quote and a free mockup.';
  };

  EnrichedPDP.prototype._whatsapp = function () {
    var num = this.whatsapp.replace(/[^0-9]/g, '');
    if (!num) return;
    window.open('https://wa.me/' + num + '?text=' + encodeURIComponent(this._quoteMessage()), '_blank');
  };

  EnrichedPDP.prototype._openLead = function (reason) {
    if (!this.lead) { this._whatsapp(); return; }
    var h = this.lead.querySelector('.bmn-epdp__lead-head');
    if (h) h.textContent = reason === 'later' ? 'Supply your logo later — free mockup' : 'See it with your logo — free mockup';
    this.lead.hidden = false;
    var f = this.lead.querySelector('[name="email"]'); if (f) setTimeout(function () { f.focus(); }, 50);
  };

  EnrichedPDP.prototype._submitLead = function () {
    var self = this;
    var name = (this.lead.querySelector('[name="name"]') || {}).value || '';
    var email = (this.lead.querySelector('[name="email"]') || {}).value || '';
    if (!name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) { this._leadSay('Please add your name and a valid email.'); return; }
    if (!this.leadUrl) { this._leadSay('Please use WhatsApp quote, or email info@brandmenow.co.uk.'); return; }
    var btn = this.lead.querySelector('.bmn-epdp__lead-submit'); if (btn) { btn.disabled = true; btn.textContent = 'Sending...'; }
    var parts = name.trim().split(/\s+/);
    var body = { fields: [
      { name: 'email', value: email.trim() },
      { name: 'firstname', value: parts.shift() || name },
      { name: 'lastname', value: parts.join(' ') },
      { name: 'message', value: this._quoteMessage() }
    ], context: { pageUri: location.href, pageName: document.title } };
    var isHs = /hsforms\.com/i.test(this.leadUrl);
    fetch(this.leadUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(isHs ? body : { name: name, email: email, message: this._quoteMessage() }) })
      .then(function (r) { return r.ok; })
      .then(function (ok) {
        if (ok) { self.lead.innerHTML = '<div class="bmn-epdp__lead-done">Thanks ' + esc(parts.length ? name.split(/\s+/)[0] : name) + '. Your request is on its way, we will send your mockup and itemised quote, usually within one working day.</div>'; }
        else self._leadSay('That did not send. Please use WhatsApp quote or email info@brandmenow.co.uk.');
      })
      .catch(function () { self._leadSay('That did not send. Please use WhatsApp quote or email info@brandmenow.co.uk.'); })
      .finally(function () { if (btn) { btn.disabled = false; btn.textContent = 'Send my request'; } });
  };
  EnrichedPDP.prototype._leadSay = function (t) { var m = this.lead.querySelector('.bmn-epdp__lead-msg'); if (m) m.textContent = t; };

  EnrichedPDP.prototype._say = function (t, type) { if (!this.msg) return; this.msg.textContent = t; this.msg.className = 'bmn-epdp__msg bmn-epdp__msg--' + type; this.msg.hidden = false; };
  EnrichedPDP.prototype._clearMsg = function () { if (this.msg) { this.msg.hidden = true; this.msg.textContent = ''; } };

  function init() {
    document.querySelectorAll('.bmn-epdp').forEach(function (el) { if (!el.dataset.epdpInit) { el.dataset.epdpInit = '1'; try { new EnrichedPDP(el); } catch (e) { console.warn('[BMN enriched PDP]', e); } } });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
